// frontend/source/portal/pages/AdminArtistList.js
import React, { useState, useEffect } from "react";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import EditArtistCard from "../components/EditArtistCard";
import CreateArtistCard from "../components/CreateArtistCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import ConfirmDialog from "../components/ConfirmDialog";
import "./AdminArtistList.css";

const AdminArtistList = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    artist: null,
  });

  useEffect(() => {
    loadArtists();
  }, []);

  const loadArtists = async () => {
    try {
      setLoading(true);
      const data = await api.getArtists();
      setArtists(data.artists || data);
      setError(null);
    } catch (err) {
      setError("Failed to load artists. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (artistId) => {
    const artist = artists.find((art) => art._id === artistId);
    setDeleteConfirm({ isOpen: true, artist });
  };

  const confirmDelete = async () => {
    try {
      await adminApi.deleteArtist(deleteConfirm.artist._id);
      setArtists(artists.filter((art) => art._id !== deleteConfirm.artist._id));
      setDeleteConfirm({ isOpen: false, artist: null });
    } catch (err) {
      setError("Failed to delete artist. Please try again.");
      console.error(err);
      setDeleteConfirm({ isOpen: false, artist: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, artist: null });
  };

  const handleUpdate = async (artistId, updateData) => {
    try {
      const updated = await adminApi.updateArtist(artistId, updateData);
      setArtists(artists.map((art) => (art._id === artistId ? updated : art)));
    } catch (err) {
      setError("Failed to update artist. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleCreate = async (artistData) => {
    try {
      const newArtist = await adminApi.createArtist(artistData);
      setArtists([newArtist, ...artists]);
      setError(null);
    } catch (err) {
      setError("Failed to create artist. Please try again.");
      console.error(err);
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading artists..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadArtists} />;
  }

  const getArtistDisplayName = (artist) => {
    return artist.fullName || `${artist.fname} ${artist.lname}`;
  };

  return (
    <div className="admin-artist-main">
      <div className="admin-artist-content">
        <h1 className="admin-artist-title">Manage Artists</h1>
        <p className="admin-artist-subtitle">Add, edit or delete artists</p>

        <div className="admin-artists-grid">
          <CreateArtistCard onCreate={handleCreate} />
          {artists.map((artist) => (
            <EditArtistCard
              key={artist._id}
              artist={artist}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      </div>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Artist?"
        message={`Are you sure you want to delete "${
          deleteConfirm.artist ? getArtistDisplayName(deleteConfirm.artist) : ""
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default AdminArtistList;
